import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { rememberedEmail, useAuth } from '../../lib/auth';
import { clearPendingLicenseCode, readPendingLicenseCode } from '../../lib/license';
import { usePageMeta } from '../../lib/seo';
import { Alert, Button, Card, Field, Icon, Input, useToast } from '../../components/ui';
import { LogoMark } from '../../components/Logo';
import { tr } from '../../lib/i18n';

interface FieldErrors {
  [key: string]: string | undefined;
}

interface DerivedError {
  general: string | null;
  fields: FieldErrors;
}

// Converte a resposta de erro do backend em mensagens por campo (quando existirem)
// e uma mensagem geral. O login com credencial inválida usa SEMPRE a mensagem única
// do backend ("E-mail ou senha incorretos."), sem revelar se o e-mail existe.
function deriveError(err: unknown): DerivedError {
  if (!(err instanceof ApiError)) {
    return { general: tr('Não foi possível concluir. Tente novamente.'), fields: {} };
  }
  const fields: FieldErrors = {};
  let general: string | null = err.message || tr('Não foi possível concluir a operação.');

  const details = err.details;
  const assign = (key: string, value: unknown): void => {
    if (typeof value === 'string') fields[key] = value;
    else if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      fields[key] = (value as string[]).join(' ');
    }
  };
  if (details && typeof details === 'object') {
    if (Array.isArray(details)) {
      for (const issue of details) {
        if (issue && typeof issue === 'object') {
          const path = (issue as { path?: unknown[] }).path;
          const message = (issue as { message?: string }).message;
          const key = Array.isArray(path) && path.length ? String(path[path.length - 1]) : '';
          if (key && message) fields[key] = message;
        }
      }
    } else {
      const record = details as Record<string, unknown>;
      const container = record.fields && typeof record.fields === 'object'
        ? (record.fields as Record<string, unknown>)
        : record;
      for (const [k, v] of Object.entries(container)) assign(k, v);
    }
  }

  switch (err.code) {
    case 'email_taken':
      fields.email = err.message;
      general = null;
      break;
    case 'terms_required':
      fields.acceptTerms = err.message;
      general = null;
      break;
    case 'invalid_category':
      fields.categoryKey = err.message;
      general = null;
      break;
    default:
      break;
  }

  return { general, fields };
}

export type LoginPortal = 'empresa' | 'admin';

/** Destino depois de entrar: a porta de administração vai para o painel administrativo; a da empresa, para o negócio. */
function landingPath(adminPortal: boolean): string {
  return adminPortal ? '/admin' : '/painel';
}

export function LoginPage({ portal = 'empresa' }: { portal?: LoginPortal }): JSX.Element {
  const admin = portal === 'admin';
  usePageMeta(
    admin ? tr('Entrar — administração') : tr('Entrar'),
    admin
      ? tr('Acesso restrito à administração da AgendaPro.')
      : tr('Acesse sua conta AgendaPro. Seu negócio. Sua agenda. Seu tempo.'),
    admin ? '/gestao' : '/entrar'
  );

  const { login, logout, loading, user, isAdmin, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  // Definido antes do login: o efeito de redirecionamento não pode navegar antes do resgate.
  const pendingLicenseRef = useRef<string | null>(readPendingLicenseCode());

  const [email, setEmail] = useState(() => rememberedEmail());
  const [remember, setRemember] = useState(() => rememberedEmail().length > 0);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldErrors>({});

  const resetNotice = (location.state as { resetDone?: boolean } | null)?.resetDone ?? false;

  useEffect(() => {
    if (!admin) return;
    const tag = document.createElement('meta');
    tag.name = 'robots';
    tag.content = 'noindex,nofollow';
    document.head.appendChild(tag);
    return () => tag.remove();
  }, [admin]);

  useEffect(() => {
    if (loading || !user) return;
    if (admin && !isAdmin) return;
    // Sessão administrativa não é desviada para /admin pela porta da empresa.
    if (!admin && isAdmin) return;
    if (!admin && pendingLicenseRef.current) return;
    navigate(landingPath(admin), { replace: true });
  }, [loading, user, isAdmin, admin, navigate]);

  const clientErrors = useMemo<FieldErrors>(() => {
    const e: FieldErrors = {};
    if (!email.trim()) e.email = tr('Informe seu e-mail.');
    if (!password) e.password = tr('Informe sua senha.');
    return e;
  }, [email, password]);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (Object.keys(clientErrors).length > 0) {
      setFields(clientErrors);
      setGeneral(null);
      return;
    }
    setSubmitting(true);
    setGeneral(null);
    setFields({});
    const pendingCode = pendingLicenseRef.current;
    try {
      // O portal vai para a API: a entrada da empresa não cria sessão para administração, e vice-versa.
      await login(email.trim(), password, remember, portal);
      if (pendingCode && !admin) {
        pendingLicenseRef.current = null;
        try {
          const res = await api.post<{ license: { planName: string; currentPeriodEnd: string } }>(
            '/api/licenses/redeem',
            { code: pendingCode }
          );
          clearPendingLicenseCode();
          await refresh();
          toast(tr('Licença ativada: plano {plan} em vigor.', { plan: res.license.planName }), 'success');
        } catch (err) {
          clearPendingLicenseCode();
          toast(
            err instanceof ApiError ? err.message : tr('Não foi possível ativar a licença.'),
            'danger'
          );
        }
        navigate('/painel', { replace: true });
        return;
      }
      toast('Bem-vindo de volta!', 'success');
      // O destino vem do useEffect abaixo, já com as associações carregadas.
    } catch (err) {
      const derived = deriveError(err);
      setGeneral(derived.general);
      setFields(derived.fields);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="mx-auto w-full max-w-content px-4 py-10 sm:py-14">
      <div className="mx-auto grid w-full max-w-md gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <LogoMark size={48} />
          <h1 className="text-3xl">{admin ? tr('Entrar na administração') : tr('Entrar na sua conta')}</h1>
          <p className="nl-hint">
            {admin
              ? tr('Acesso restrito aos administradores da AgendaPro.')
              : tr('Entrada dos profissionais. Seu negócio, sua agenda, seus clientes.')}
          </p>
        </div>

        {resetNotice ? (
          <Alert tone="success" title={tr("Senha redefinida")}>
            {tr("Sua senha foi alterada com sucesso e todas as sessões antigas foram encerradas. Entre novamente com a nova senha.")}
          </Alert>
        ) : null}

        {!admin && isAdmin ? (
          <Card as="section">
            <Alert tone="info" title={tr("Sessão de administração ativa")}>
              {tr("Esta é a entrada dos profissionais. Saia da administração para entrar com a conta do seu estabelecimento.")}
            </Alert>
            <div className="mt-4">
              <Button
                className="w-full"
                onClick={() => void logout().then(() => navigate('/entrar', { replace: true }))}
              >
                {tr("Sair e entrar como empresa")}
              </Button>
            </div>
          </Card>
        ) : (
          <Card as="section">
            <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
              {general ? <Alert tone="danger" title={tr("Não foi possível entrar")}>{general}</Alert> : null}

              <Field label={tr("E-mail")} error={fields.email} required>
                {(p) => (
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder={tr("voce@exemplo.com")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                    invalid={Boolean(fields.email)}
                    {...p}
                  />
                )}
              </Field>

              <Field label={tr("Senha")} error={fields.password} required>
                {(p) => (
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={submitting}
                      invalid={Boolean(fields.password)}
                      className="pr-24"
                      {...p}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-2 my-auto h-fit rounded px-2 py-1 text-sm text-muted hover:text-graphite"
                      aria-pressed={showPassword}
                    >
                      {showPassword ? tr('Ocultar') : tr('Mostrar')}
                    </button>
                  </div>
                )}
              </Field>

              <div>
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-[var(--nl-graphite)]"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    disabled={submitting}
                  />
                  <span>
                    {tr("Ficar conectado neste dispositivo")}
                    <span className="mt-0.5 block text-xs text-muted">
                      {tr("Mantém sua sessão ativa por até 30 dias e lembra seu e-mail. A senha é guardada pelo gerenciador do navegador, nunca pela AgendaPro. Use apenas em aparelho seu.")}
                    </span>
                  </span>
                </label>
              </div>

              <Button type="submit" loading={submitting} disabled={submitting} className="w-full">
                {tr("Entrar")}
              </Button>

              <div className="flex justify-end">
                <Link to="/esqueci-senha" className="text-sm font-medium text-graphite hover:underline">
                  {tr("Esqueci minha senha")}
                </Link>
              </div>
            </form>
          </Card>
        )}

        {!admin ? (
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="nl-hint">{tr("Ainda não é profissional?")}</p>
            <Button onClick={() => navigate('/comecar')} disabled={submitting}>
              {tr("Começar teste grátis")}
            </Button>
          </div>
        ) : null}
        <p className="mt-1 flex items-center justify-center gap-1 text-xs text-muted">
          <Icon name="shield" size={14} /> {tr("Ambiente seguro: guardamos apenas um hash da senha e um cookie de sessão.")}
        </p>
      </div>
    </section>
  );
}
